import React from 'react';
import { Box, Alert } from '@mui/material';
import SignupStep1AccountDetails from './SignupStep1AccountDetails';
import SignupStep2ProfileInfo from './SignupStep2ProfileInfo';
import SignupStep3Confirmation from './SignupStep3Confirmation';
import SignupStep4Complete from './SignupStep4Complete';
import CustomStepper from '../common/CustomStepper';
import { useThemeContext } from '../../contexts/BaseThemeContextProvider';

interface RegistrationStepperProps {
  activeStep: number;
  error: string | null;
  onSubmit: (values: any) => void;
  onComplete: () => void;
  email: string;
  isSubmitting: boolean;
  onResendCode: (setResendSubmitting: any, setMessage: any) => void;
  fetchAppointmentDetails: (token: string, setApptDetailsNotFound: any, setLoading: any) => void;
  fetchClientBookingDetails: (token: string, setClientBookingDetailsNotFound: any, setLoading: any) => void;
  fetchFormLinks: (token: string, setFormLinksNotFound: any, setLoading: any) => void;
  fetchInviteDetails: (inviteToken: string, setInviteNotFound: any, setLoading: any) => void;
  inviteDetails: any;
  inviteAcceptanceStatus: 'idle' | 'success' | 'error' | 'skipped';
  inviteAcceptanceError: string | null;
}

const RegistrationStepper: React.FC<RegistrationStepperProps> = ({
  activeStep,
  error,
  onSubmit,
  onComplete,
  email,
  isSubmitting,
  onResendCode,
  fetchAppointmentDetails,
  fetchClientBookingDetails,
  fetchFormLinks,
  fetchInviteDetails,
  inviteDetails,
  inviteAcceptanceStatus,
  inviteAcceptanceError,
}) => {
  const { theme } = useThemeContext();
  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <SignupStep1AccountDetails
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            fetchAppointmentDetails={fetchAppointmentDetails}
            fetchClientBookingDetails={fetchClientBookingDetails}
            fetchInviteDetails={fetchInviteDetails}
          />
        );
      case 1:
        return <SignupStep2ProfileInfo onSubmit={onSubmit} isSubmitting={isSubmitting} />;
      case 2:
        return (
          <SignupStep3Confirmation
            email={email}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            onResendCode={onResendCode}
          />
        );
      case 3:
        return (
          <SignupStep4Complete
            onComplete={onComplete}
            fetchFormLinks={fetchFormLinks}
            fetchClientBookingDetails={fetchClientBookingDetails}
            inviteDetails={inviteDetails}
            inviteAcceptanceStatus={inviteAcceptanceStatus}
            inviteAcceptanceError={inviteAcceptanceError}
          />
        );
      default:
        return 'Unknown step';
    }
  };

  const renderStepperHeaders = () => {
    if (activeStep === 3) return;

    return (
      <Box sx={{ mb: 3, mt: 4 }}>
        <CustomStepper steps={4} activeStep={activeStep + 1} />
      </Box>
    );
  };

  return (
    <Box
      sx={{
        padding: '34px',
        paddingTop: '0px',
        backgroundColor: theme.custom.components.registrationStepper.backgroundColor,
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {renderStepperHeaders()}
      <React.Fragment>{getStepContent(activeStep)}</React.Fragment>
    </Box>
  );
};

export default RegistrationStepper;
